/**
 * ****************************************************************************
 * @description     :   Global Popup Utilities
 * @created at      :   2025/08/01
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import store from '@/store';

/**
 * Show popup with component
 * @param {Object} config - Popup configuration
 * @param {Component} config.component - Vue component to show
 * @param {Object} config.componentProps - Props to pass to component
 * @param {String} config.title - Popup title
 * @param {Object} config.dimensions - Size configuration
 * @param {Object} config.styles - Style configuration
 * @param {Object} config.classes - Class configuration
 * @param {Object} config.options - Options configuration
 * @param {Object} config.customComponents - Custom header components configuration
 * @param {Boolean} config.isStillShowPopupBefore - Keep previous popups visible (default: false)
 *                                        When true, new popup will stack on top (all popups visible)
 *                                        When false, previous popups are hidden (can be restored on close)
 * @param {Function} config.onClose - Close callback
 * @param {Function} config.onSelect - Select callback
 * @param {Object} config.eventHandlers - Additional event handlers object
 *
 * @example
 * // Single popup (default behavior - replaces all popups)
 * showPopup({
 *     component: MyComponent,
 *     title: 'My Popup'
 * });
 *
 * @example
 * // Stack popups - multiple popups visible at once
 * showPopup({
 *     component: FirstComponent,
 *     title: 'First Popup',
 *     isStillShowPopupBefore: true
 * });
 *
 * showPopup({
 *     component: SecondComponent,
 *     title: 'Second Popup',
 *     isStillShowPopupBefore: true
 * });
 * // Now both popups are visible, with SecondPopup on top
 * // Clicking overlay or pressing ESC will only close the topmost popup (SecondPopup)
 */
export const showPopup = (config) => {
    store.commit('app/showPopup', config);
};

/**
 * Hide current popup
 */
export const hidePopup = () => {
    store.commit('app/hidePopup');
};

/**
 * Hide all popups (including stack)
 */
export const hideAllPopups = () => {
    store.commit('app/hideAllPopups');
};

/**
 * Update popup configuration
 * @param {Object} config - Configuration to update
 */
export const updatePopupConfig = (config) => {
    store.commit('app/updatePopupConfig', config);
};

/**
 * Preset configurations for common popup types
 */
export const POPUP_PRESETS = {
    // Small popup
    SMALL: {
        dimensions: {
            width: '400px',
            height: '300px'
        }
    },

    // Medium popup
    MEDIUM: {
        dimensions: {
            width: '600px',
            height: '400px'
        }
    },

    // Large popup
    LARGE: {
        dimensions: {
            width: '90%',
            height: '90%',
            maxWidth: '1200px'
        }
    },

    // Full screen popup
    FULLSCREEN: {
        dimensions: {
            width: '95%',
            height: '95%'
        }
    },

    // No header popup
    NO_HEADER: {
        options: {
            showHeader: false,
            showCloseButton: false,
            pureContent: true
        }
    },

    // Modal popup (can't close by clicking overlay)
    MODAL: {
        options: {
            closeOnOverlay: false,
            closeOnEscape: false
        }
    }
};

/**
 * Helper function to show popup with preset
 * @param {String} preset - Preset name from POPUP_PRESETS
 * @param {Object} config - Additional configuration
 */
export const showPopupWithPreset = (preset, config = {}) => {
    const presetConfig = POPUP_PRESETS[preset] || {};
    const mergedConfig = {
        ...presetConfig,
        ...config,
        dimensions: { ...presetConfig.dimensions, ...config.dimensions },
        styles: { ...presetConfig.styles, ...config.styles },
        classes: { ...presetConfig.classes, ...config.classes },
        options: { ...presetConfig.options, ...config.options }
    };

    showPopup(mergedConfig);
};

// Vue Plugin to add global methods
export default {
    install(app) {
        app.config.globalProperties.$showPopup = showPopup;
        app.config.globalProperties.$hidePopup = hidePopup;
        app.config.globalProperties.$hideAllPopups = hideAllPopups;
        app.config.globalProperties.$showPopupWithPreset = showPopupWithPreset;
    }
};
